import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { filter } from 'rxjs/operators';
import { AppService } from 'src/app/core';
import { RouteList } from 'src/app/core/common.model';
import { LoggingService } from 'src/app/core/services/logging.service';
import { AccessManegerService } from 'src/app/views/access-manager/access-manager.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-side-navbar',
  templateUrl: './side-navbar.component.html',
  styleUrls: ['./side-navbar.component.scss'],
  standalone: false
})
export class SideNavbarComponent implements OnInit {
  @ViewChild('sidebarMiddle', { static: false })
  sidebarMiddle!: ElementRef<HTMLElement>;

  readonly categoryMap = new Map<string, boolean>([
    ['Redeem Rules', environment.enableRedeemRules],
    ['Tiers', environment.enableTiers],
    ['Subscriptions', environment.enableSubscriptions],
    ['Store Settings', environment.enableStoreSettings],
    ['Bulk Tier Update', environment.enablePredefinedTierAssignment],
    ['Webhooks', environment.enableWebhooks]
  ]);

  categories: RouteList[] = [];
  topCategories: RouteList[] = [];
  bottomCategories: RouteList[] = [];
  isShow = true;
  isEmbeddedView = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(LoggingService);

  constructor(
    private readonly appService: AppService,
    private readonly router: Router,
    private readonly keycloakService: KeycloakService,
    private readonly accessManagerService: AccessManegerService
  ) {}

  /**
   * Logo URL stream from AppService.
   * - On login: AppComponent calls loadTenantLogo() which fetches the image
   *   via HttpClient (auth interceptors included) and emits the blob URL.
   * - Default: emits 'assets/images/sidenav/jio.svg' until the fetch resolves.
   * - On Customize save: refreshTenantLogo() re-fetches and emits a new blob.
   */
  readonly tenantLogoUrl$ = this.appService.tenantLogoUrl$;

  get tenantName(): string {
    return environment.workspaceName ?? 'JioLoyalty';
  }

  ngOnInit(): void {
    this.categories = this.appService.getSideBarNav();
    this.categories.forEach((cat) => {
      cat.canShow = true;
      if (this.categoryMap.has(cat.name)) {
        cat.canShow = this.categoryMap.get(cat.name);
      }
      cat.subMenu?.forEach((subCat) => {
        subCat.canShow = true;
        if (this.categoryMap.has(subCat.name)) {
          subCat.canShow = this.categoryMap.get(subCat.name);
        }
      });
    });
    this.topCategories = this.categories.filter((c) => !c.stickToBottom);
    this.bottomCategories = this.categories.filter((c) => c.stickToBottom);

    this.accessManagerService
      .getProfileDetail()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!response.areAllMerchantsAllowed) {
          this.topCategories = this.topCategories.filter(
            (cat) => cat.name !== 'Subscriptions'
          );
        }
      });

    this.appService.isEmbeddedViewasObservable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((bool) => {
        this.isEmbeddedView = bool;
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const currentPath = this.router.url.split('?')[0];
        const currentRoute = currentPath?.split('/')[1];

        let currentActive = this.categories.find((f) =>
          f.link.startsWith(String(currentRoute))
        );
        if (!currentActive) {
          currentActive = this.categories.find((f) =>
            f.subMenu?.some((r) => r.link.startsWith(String(currentRoute)))
          );
        }
        if (currentActive) currentActive.isExpand = true;
      });
  }

  closeAllExpansionPanels(): void {
    this.categories.forEach((f) => {
      if (f.subMenu) f.isExpand = false;
    });
  }

  handleAccordion(routeObject: RouteList): void {
    const isAlreadyOpen = routeObject.isExpand;
    // Expand sidebar if collapsed
    if (!this.isShow) {
      this.isShow = true;
    }
    this.categories.forEach((c) => {
      if (c.subMenu) c.isExpand = false;
    });
    routeObject.isExpand = !isAlreadyOpen;
  }

  isGroupActive(c: RouteList): boolean {
    return c.subMenu?.some((sub) => this.isRouteActive(sub)) ?? false;
  }

  logout(): void {
    // Invalidate the Keycloak session server-side first so the bearer token cannot be reused
    // after this browser is gone. If the server call fails we still complete the Keycloak
    // logout so the user is not stranded in a half-logged-out state.
    this.accessManagerService.logout().subscribe({
      next: () => this.completeKeycloakLogout(),
      error: (error: unknown) => {
        this.logger.error('Server-side logout failed', error);
        this.completeKeycloakLogout();
      }
    });
  }

  private completeKeycloakLogout(): void {
    this.keycloakService
      .logout(window.location.origin)
      .catch((error: unknown) => this.logger.error('Logout failed', error));
  }

  toggleMe(): void {
    this.isShow = !this.isShow;
    // Collapse all open groups when collapsing sidebar
    if (!this.isShow) {
      this.closeAllExpansionPanels();
    }
  }

  goToHome(): void {
    this.router
      .navigate(['/'])
      .catch((error: unknown) => this.logger.log('Navigate home', error));
  }

  isRouteActive(subMenu: RouteList): boolean {
    const currentPath = this.router.url.split('?')[0];
    const subMenuPath = `/${subMenu.link}`;

    if (subMenuPath === '/approvals') {
      return (
        currentPath === '/approvals' ||
        (currentPath?.startsWith('/approvals/approval-setup') ?? false)
      );
    }

    return currentPath?.startsWith(subMenuPath) ?? false;
  }
}
